import apiClient from './client';
import { openPdfInTab } from '../utils/pdfPreview';
import type { Omologa, OmologaList, OmologaStato } from '../types/omologa';

export interface OmologheFilters {
  page?: number;
  limit?: number;
  produttore_id?: string;
  stato?: Exclude<OmologaStato, 'indeterminata'>;
}

export interface OmologaCreatePayload {
  produttore_id: string;
  // numero_omologa autogenerato per produttore (#134): non si invia più.
  numero_conferimenti?: number | null;
  frequenza?: string | null;
  pezzatura?: string | null;
  note?: string | null;
  data_firma?: string | null;
  data_scadenza?: string | null;
  trasportatori_ids?: string[];
  intermediari_ids?: string[];
}

export interface OmologaUpdatePayload {
  // numero_omologa immutabile (autogenerato #134).
  numero_conferimenti?: number | null;
  frequenza?: string | null;
  pezzatura?: string | null;
  note?: string | null;
  data_firma?: string | null;
  data_scadenza?: string | null;
  trasportatori_ids?: string[];
  intermediari_ids?: string[];
}

export async function listOmologhe(filters?: OmologheFilters): Promise<OmologaList> {
  const params: Record<string, string> = {};
  if (filters?.page != null) params['page'] = String(filters.page);
  if (filters?.limit != null) params['limit'] = String(filters.limit);
  if (filters?.produttore_id) params['produttore_id'] = filters.produttore_id;
  if (filters?.stato) params['stato'] = filters.stato;
  const { data } = await apiClient.get<OmologaList>('/api/omologhe', { params });
  return data;
}

export async function getOmologa(id: string): Promise<Omologa> {
  const { data } = await apiClient.get<Omologa>(`/api/omologhe/${id}`);
  return data;
}

export async function createOmologa(payload: OmologaCreatePayload): Promise<Omologa> {
  const { data } = await apiClient.post<Omologa>('/api/omologhe', payload);
  return data;
}

export async function updateOmologa(id: string, payload: OmologaUpdatePayload): Promise<Omologa> {
  const { data } = await apiClient.patch<Omologa>(`/api/omologhe/${id}`, payload);
  return data;
}

export async function deleteOmologa(id: string): Promise<void> {
  await apiClient.delete(`/api/omologhe/${id}`);
}

// ── Ciclo di vita firma (Blocco 6 STEP 4) ────────────────────────────────────

export interface InviaOmologaPayload {
  destinatario_email?: string;
  oggetto?: string;
  corpo?: string;
}

export async function generaPdfOmologa(id: string): Promise<Omologa> {
  const { data } = await apiClient.post<Omologa>(`/api/omologhe/${id}/genera-pdf`);
  return data;
}

export async function inviaOmologa(
  id: string,
  payload: InviaOmologaPayload = {},
): Promise<Omologa> {
  const { data } = await apiClient.post<Omologa>(`/api/omologhe/${id}/invia`, payload);
  return data;
}

export async function caricaFirmatoOmologa(id: string, file: File): Promise<Omologa> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<Omologa>(
    `/api/omologhe/${id}/carica-firmato`,
    formData,
  );
  return data;
}

// ── Download PDF (endpoint autenticato, NON path /storage statico) ────────────
// Riuso dell'helper condiviso openPdfInTab (utils/pdfPreview): fetch con token
// Bearer + apertura blob in nuova tab. Stesso pattern di InviaClienteModal.

export async function apriSchedaPdfOmologa(id: string): Promise<void> {
  await openPdfInTab(`/api/omologhe/${id}/scheda-pdf`);
}

export async function apriFirmatoPdfOmologa(id: string): Promise<void> {
  await openPdfInTab(`/api/omologhe/${id}/firmato-pdf`);
}
